/**
 * Organization tab (tenant_owner only) — logo + name.
 *
 * The logo is upload-only (no pasted URL — that path silently lost to an uploaded
 * logo and was retired) and saves immediately via its own endpoints. Brand colours
 * are NOT here: they live in Web Chats → Appearance, the only place they render.
 * The name is read-first with an explicit Save, like the profile.
 */
import { Button, CircularProgress, Grid, TextField } from '@mui/material';
import { Building2, ImagePlus, Pencil } from 'lucide-react';
import React, { useState } from 'react';

import AccountCard, { DetailRow } from '@/components/account/AccountCard';
import ImageUploader from '@/components/account/ImageUploader';
import apiService from '@/services/api.service';

import { extractError, imageProblem, Notify, uploadedUrl } from './shared';

interface Props {
  orgName: string;
  logoUrl: string;
  onNameSaved: (name: string) => void;
  onLogoChange: (url: string) => void;
  notify: Notify;
}

const OrganizationPanel: React.FC<Props> = ({
  orgName,
  logoUrl,
  onNameSaved,
  onLogoChange,
  notify,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState<'uploading' | 'removing' | null>(null);

  const pickLogo = async (file: File) => {
    const problem = imageProblem(file);
    if (problem) return notify(problem, 'error');
    setLogoBusy('uploading');
    try {
      const form = new FormData();
      form.append('logo', file);
      // axios defaults to application/json — override so the multipart boundary is set (see api.service.ts).
      const res: any = await apiService.post('/auth/organization/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onLogoChange(uploadedUrl(res, (d) => d?.branding?.logoUrl));
      notify('Logo updated', 'success');
    } catch (error: any) {
      notify(extractError(error, 'Failed to upload logo'), 'error');
    } finally {
      setLogoBusy(null);
    }
  };

  const removeLogo = async () => {
    setLogoBusy('removing');
    try {
      await apiService.delete('/auth/organization/logo');
      onLogoChange('');
      notify('Logo removed', 'success');
    } catch (error: any) {
      notify(extractError(error, 'Failed to remove logo'), 'error');
    } finally {
      setLogoBusy(null);
    }
  };

  const startEdit = () => {
    setDraft(orgName);
    setTouched(false);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setTouched(false);
  };
  const name = draft.trim();
  const error = name ? '' : 'Organization name is required';
  const dirty = name !== orgName;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (error || !dirty || saving) return;
    setSaving(true);
    try {
      await apiService.patch('/auth/organization', { name });
      onNameSaved(name);
      setEditing(false);
      notify('Organization updated', 'success');
    } catch (err: any) {
      notify(extractError(err, 'Failed to update organization'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <AccountCard
          icon={<ImagePlus size={18} strokeWidth={1.75} />}
          title="Logo"
          subtitle="Shown in your workspace and in team invites."
        >
          <ImageUploader
            {...(logoUrl ? { src: logoUrl } : {})}
            fallback={<Building2 size={32} strokeWidth={1.5} />}
            shape="rounded"
            size={88}
            busy={logoBusy}
            uploadLabel="Upload logo"
            noun="organization logo"
            hint="PNG, JPG or WebP, square, at least 256×256 and up to 2MB."
            onPick={pickLogo}
            onRemove={removeLogo}
          />
        </AccountCard>
      </Grid>

      <Grid item xs={12} md={7}>
        <AccountCard
          icon={<Building2 size={18} strokeWidth={1.75} />}
          title="Organization profile"
          subtitle="Visible to your whole team."
          {...(editing
            ? {
                onSubmit: save,
                onEscape: cancel,
                footer: (
                  <>
                    <Button
                      onClick={cancel}
                      disabled={saving}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving || !dirty}
                      sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
                    >
                      {saving ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                      ) : (
                        'Save organization'
                      )}
                    </Button>
                  </>
                ),
              }
            : {
                action: (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={startEdit}
                    startIcon={<Pencil size={15} strokeWidth={1.75} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'divider',
                      color: 'text.primary',
                    }}
                  >
                    Edit
                  </Button>
                ),
              })}
        >
          {editing ? (
            <TextField
              label="Organization name"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoComplete="organization"
              autoFocus
              fullWidth
              required
              error={touched && Boolean(error)}
              helperText={touched && error ? error : ' '}
            />
          ) : (
            <DetailRow label="Organization name">{orgName}</DetailRow>
          )}
        </AccountCard>
      </Grid>
    </Grid>
  );
};

export default OrganizationPanel;
